import { mat4, ReadonlyVec2, vec2, vec3, vec4 } from 'gl-matrix';
import { DicomProcessor } from '../../domain/services/dicomProcessor';
import { Environment } from '../data/Environment';
import { ModifierKeys } from '../tool/ModifierKeys';
import { ToolManager } from '../tool/ToolManager';
import { TextureView } from './TextureView';
import { ViewUpdateEvent } from './events/ViewUpdateEvent';
import { LayerBit } from './layers/LayerBit';
import { PlaneView } from './views/PlaneView';
import { RigidRegistration } from '~/domain/patient/rigidRegistration';

/**
 * Class that manages all the {@link View Views}, their positions and scales.
 */
export class ViewManager implements EventListenerObject, EventTarget {
    /**
     * The {@link ViewManager} singleton.
     */
    private static manager: ViewManager | undefined = undefined;

    private readonly _env: Environment;

    private _secondaryEnv: Environment | null = null;

    /**
     * The {@link HTMLElement} on which all the views should be populated.\
     */
    private parentElement!: HTMLElement;

    /**
     * The {@link OffscreenCanvas} that provides us the {@link WebGL2RenderingContext WebGL context}.\
     * This context is created within {@link initializeViewManager}-method\
     * and stored inside the global {@link gl}-variable.
     */
    private readonly canvas: OffscreenCanvas;

    /**
     * Currently active {@link View}.\
     * This may be undefined if there's not currently active {@link View}.
     */
    private _activeView: PlaneView | undefined;

    /**
     * {@link Array} of {@link View}s that this {@link ViewManager} holds.
     */
    private readonly _views: Array<PlaneView> = [];

    private readonly _secondaryViews: Array<PlaneView> = [];

    private _locked: boolean = false;

    public get locked(): boolean {
        return this._locked;
    }

    private constructor(
        dicomProcessor: DicomProcessor,
        width: number,
        height: number
    );

    private constructor(dicomProcessor: DicomProcessor, element: HTMLElement);

    /**
     * Private constructor that will create {@link ViewManager}.\
     * Initializing the {@link canvas} with size equal to that of the {@link parentElement}'s.\
     * And {@link ViewManager.setParent set the parent element}.
     * @param parentElement {@link HTMLElement} on which all the {@link View Views} handled by this {@link ViewManager} should reside.
     */
    private constructor(
        private readonly dicomProcessor: DicomProcessor,
        arg0: HTMLElement | number,
        height?: number
    ) {
        if (arg0 instanceof HTMLElement) {
            this.canvas = new OffscreenCanvas(1, 1);
            this.setParent(arg0);
        } else {
            this.canvas = new OffscreenCanvas(arg0, height as number);
        }
        this._env = new Environment(this);
    }

    public get env(): Environment {
        return this._env;
    }

    public get secondaryEnvironment(): Environment | null {
        return this._secondaryEnv;
    }

    public enableSecondaryEnvironment() {
        if (!this._secondaryEnv) {
            this._secondaryEnv = new Environment(this);
        }
    }

    public disableSecondaryEnvironment() {
        if (this._secondaryEnv) {
            this._secondaryEnv = null;
        }
    }

    /**
     * Implementation of {@link EventTarget.addEventListener}.\
     * @param type
     * @param callback
     * @param options
     */
    public addEventListener(
        type: string,
        callback: EventListenerOrEventListenerObject,
        options?: boolean | AddEventListenerOptions | undefined
    ): void {
        this.parentElement.addEventListener(type, callback, options);
    }

    /**
     * Implementation of {@link EventTarget.dispatchEvent}.\
     * @param event
     */
    public dispatchEvent(event: Event): boolean {
        return this.parentElement.dispatchEvent(event);
    }

    /**
     * Implementation of {@link EventTarget.removeEventListener}.\
     * @param type
     * @param callback
     * @param options
     */
    public removeEventListener(
        type: string,
        callback: EventListenerOrEventListenerObject,
        options?: boolean | EventListenerOptions | undefined
    ): void {
        this.parentElement.removeEventListener(type, callback, options);
    }

    /**
     * Method to map a point in the primary {@link Environment} to the secondary {@link Environment} via {@link RigidRegistration}.
     * @param primaryEnv
     * @param secondaryEnv
     * @param registration
     */
    public mapPrimaryToSecondaryPatient(
        primaryEnv: Environment,
        secondaryEnv: Environment,
        registration: RigidRegistration
    ): vec3 | null {
        if (
            !primaryEnv?.image ||
            !secondaryEnv?.image ||
            !registration?.transformation
        ) {
            return null;
        }
        const primaryPt = primaryEnv.planeIntersectionPoint.coordinates as vec3;

        const pt4 = vec4.fromValues(
            primaryPt[0],
            primaryPt[1],
            primaryPt[2],
            1
        );
        const mapped4 = vec4.create();

        const transform = mat4.invert(
            mat4.create(),
            registration.transformation
        );
        if (!transform) {
            return null;
        }
        vec4.transformMat4(mapped4, pt4, transform);

        const secondaryPt = vec3.fromValues(mapped4[0], mapped4[1], mapped4[2]);

        const voxel = secondaryEnv.image.grid.toVoxelSpace(secondaryPt);

        const clampedVoxel = vec3.fromValues(
            Math.min(Math.max(voxel[0], 0), secondaryEnv.image.grid.sizeX - 1),
            Math.min(Math.max(voxel[1], 0), secondaryEnv.image.grid.sizeY - 1),
            Math.min(Math.max(voxel[2], 0), secondaryEnv.image.grid.sizeZ - 1)
        );

        return secondaryEnv.image.grid.voxelCenterToWorldSpace(clampedVoxel);
    }

    /**
     * Method to set whether the {@link ViewManager} is {@link locked}.\
     * When {@link locked}, the {@link secondaryEnvironment} will follow the {@link env primary environment}.\
     * @param value Whether the {@link ViewManager} should be {@link locked}.
     */
    public setLocked(value: boolean) {
        this._locked = value;
        if (this._locked) {
            if (this._secondaryEnv && this._secondaryEnv.registration) {
                this.syncPanZoomToSecondary(this._env, this._secondaryEnv);
                const mappedPoint = this.mapPrimaryToSecondaryPatient(
                    this._env,
                    this._secondaryEnv,
                    this._secondaryEnv.registration
                );
                if (mappedPoint) {
                    this._secondaryEnv.planeIntersectionPoint.coordinates =
                        mappedPoint;
                }
            }
        }
    }

    /**
     * Method to synchronize the {@link panZoom} of the {@link secondaryEnvironment} to that of the {@link environment}.\
     * This will only work if the {@link ViewManager} is {@link locked} and the {@link secondaryEnvironment} has a {@link RigidRegistration}.
     * @param environment The primary {@link Environment}, which {@link panZoom} should be synchronized to.
     * @param secondaryEnvironment The secondary {@link Environment}, which {@link panZoom} should be synchronized.
     */
    public syncPanZoomToSecondary(
        environment: Environment,
        secondaryEnvironment: Environment
    ) {
        if (this._locked && secondaryEnvironment.registration) {
            secondaryEnvironment.panZoom.scale = environment.panZoom.scale;
        }
    }

    /**
     * Method to set the {@link ViewManager.parentElement parentElement} of this {@link ViewManager}.\
     * All the {@link View Views} held by this {@link ViewManager} should reside on that {@link HTMLElement parentElement}.
     * @param parentElement The parent {@link HTMLElement element} on which all the {@link View Views} should reside.
     */
    private setParent(parentElement: HTMLElement): void {
        if (this.parentElement) {
            this.parentElement.removeEventListener('mouseup', this);
            this.parentElement.removeEventListener('mousedown', this);
            this.parentElement.removeEventListener('mousemove', this);
            this.parentElement.removeEventListener('mouseleave', this);
            this.parentElement.removeEventListener('mouseenter', this);
            this.parentElement.removeEventListener('wheel', this);
            this.parentElement.removeEventListener('drop', this);
            this.parentElement.removeEventListener('dragover', this);
            this.parentElement.removeEventListener('keydown', this);
            this.parentElement.removeEventListener('keyup', this);
            this.parentElement.removeEventListener('dblclick', this);
            this.parentElement.removeEventListener('contextmenu', this);
            document.removeEventListener('keydown', this.onDocumentKeyDown);
            document.removeEventListener('keyup', this.onDocumentKeyUp);
        }
        this.parentElement = parentElement;
        this.parentElement.tabIndex = -1;
        parentElement.addEventListener('mouseup', this);
        parentElement.addEventListener('mousedown', this);
        parentElement.addEventListener('mousemove', this);
        parentElement.addEventListener('mouseleave', this);
        parentElement.addEventListener('mouseenter', this);
        parentElement.addEventListener('wheel', this);
        parentElement.addEventListener('drop', this);
        parentElement.addEventListener('dragover', this);
        parentElement.addEventListener('keydown', this);
        parentElement.addEventListener('keyup', this);
        parentElement.addEventListener('dblclick', this);
        parentElement.addEventListener('contextmenu', this);
        document.addEventListener('keydown', this.onDocumentKeyDown);
        document.addEventListener('keyup', this.onDocumentKeyUp);
    }

    private isTextInputTarget(event: KeyboardEvent): boolean {
        const target = event.target as HTMLElement;
        const tagName = target.tagName;
        return (
            tagName === 'INPUT' ||
            tagName === 'TEXTAREA' ||
            target.isContentEditable
        );
    }

    private onDocumentKeyDown = (event: KeyboardEvent): void => {
        if (this.isTextInputTarget(event)) {
            return;
        }
        const consumed = ToolManager.onPanKeyDown(event.key);
        if (consumed && this._activeView) {
            event.preventDefault();
        }
    };

    private onDocumentKeyUp = (event: KeyboardEvent): void => {
        // Always clear pan key state (even in text inputs)
        // to prevent stuck state when a key was pressed
        // outside a text input and released inside one.
        const consumed = ToolManager.onPanKeyUp(event.key);
        if (consumed && !this.isTextInputTarget(event)) {
            event.preventDefault();
        }
    };

    public resetViews() {
        for (const env of [this._env, this._secondaryEnv]) {
            if (!env) {
                continue;
            }
            env.panZoom.scale = 1;

            if (env.image) {
                env.planeIntersectionPoint.coordinates =
                    env.image.centerVoxelCenter;
                this.dispatchEvent(new Event('planeIntersectionPointChanged'));
            }
        }

        for (const view of this.allViews) {
            const img = view.environment?.image;
            if (img) {
                view.fitToView(img.boundingBox);
                view.updateZoomScale(view.environment.panZoom.scale);
            }
        }
        for (const view of this.allViews) {
            view.updateView(LayerBit.ALL_LAYERS);
        }
    }

    public static initializeViewManager(
        dp: DicomProcessor,
        parent: HTMLElement
    ): [ViewManager, WebGL2RenderingContext];

    public static initializeViewManager(
        dp: DicomProcessor,
        width: number,
        height: number
    ): [ViewManager, WebGL2RenderingContext];

    /**
     * Method to initialize the {@link ViewManager.manager ViewManager singleton}.\
     * This method will also {@link Canvas.getContext initialize} the {@link WebGL2RenderingContext}.
     * @param arg0 Either {@link HTMLElement} that should be the parent of this._views {@link ViewManager} or\
     * width in pixels that this {@link ViewManager} should have after initialization.
     * @param height Optional parameter that should be provided if the first argument is number.\
     * This is the height this {@link ViewManager} should have after initialization.
     * @returns A {@link WebGL2RenderingContext}, which is the WebGL context of the {@link canvas OffscreenCanvas}.
     */
    public static initializeViewManager(
        dp: DicomProcessor,
        arg0: HTMLElement | number,
        height?: number
    ): [ViewManager, WebGL2RenderingContext] {
        if (!ViewManager.manager) {
            ViewManager.manager =
                arg0 instanceof HTMLElement
                    ? new ViewManager(dp, arg0)
                    : new ViewManager(dp, arg0, height as number);
        } else if (arg0 instanceof HTMLElement) {
            ViewManager.manager.setParent(arg0);
        } else {
            throw new Error(
                `Attempt to initialize ViewManager with size ${arg0}x${height}, while ViewManager has already been initialized`
            );
        }
        const context = ViewManager.manager.canvas.getContext('webgl2', {
            antialias: false,
            stencil: true,
        });
        if (!context) {
            throw new Error('WebGL 2 is not available');
        }
        return [ViewManager.manager, context as WebGL2RenderingContext];
    }

    /**
     * The pixel width of the {@link ViewManager}, which is equal to the pixel width of the {@link ViewManager.parentElement parentElement}.
     * @returns The width of {@link ViewManager} in pixels
     */
    public get width(): number {
        return this.parentElement.clientWidth;
    }

    /**
     * The pixel height of the {@link ViewManager}, which is equal to the pixel height of the {@link ViewManager.parentElement parentElement}.
     * @returns The height of {@link ViewManager} in pixels
     */
    public get height(): number {
        return this.parentElement.clientHeight;
    }

    /**
     * The pixel size of the {@link ViewManager}, which is equal to the pixel size of the {@link ViewManager.parentElement parentElement}.
     * @returns The size of {@link ViewManager} in pixels.
     */
    public get sizePx(): vec2 {
        return vec2.fromValues(
            this.parentElement.clientWidth,
            this.parentElement.clientHeight
        );
    }

    private get allViews(): PlaneView[] {
        return [...this._views, ...this._secondaryViews];
    }

    public addView(view: PlaneView) {
        this._views.push(view);
    }

    public addSecondaryView(view: PlaneView) {
        this._secondaryViews.push(view);
    }

    public removeView(view: PlaneView) {
        const index = this._views.findIndex((v) => v === view);
        if (index >= 0) {
            this._views[index].delete();
            this._views.splice(index, 1);
        }
    }

    public removeSecondaryView(view: PlaneView) {
        const index = this._secondaryViews.findIndex((v) => v === view);
        if (index >= 0) {
            this._secondaryViews[index].delete();
            this._secondaryViews.splice(index, 1);
        }
    }

    /**
     * Method to update the currently available views.\
     * The {@link event} will determine whether a {@link View} should be updated or not.\
     * This method will loop through all the views,\
     * inspecting if the {@link View} should be updated by the {@link event}\
     * and returns the number of updated {@link View}s.
     * @param event {@link ViewUpdateEvent} controlling which {@link View Views} are to be updated and in what ways.
     * @returns The number of updated {@link View views}. Whole number between 0 and {@link views.size}.
     */
    private updateViews(event: ViewUpdateEvent): number {
        let numberOfUpdatedViews = 0;
        for (const view of this.allViews) {
            if (event.updateView(view)) {
                ++numberOfUpdatedViews;
            }
        }
        return numberOfUpdatedViews;
    }

    /**
     * Method to get the {@link View} that is located at the provided {@link coordinates}.\
     * Undefined if none of the views overlaps with those coordinates.
     * @param coordinates PixelCoordinates that the {@link View} should cover.
     * @returns The {@link View} that covers the {@link coordinates}.\
     * Undefined if none of the views covers those coordinates.
     */
    private getView(coordinates: ReadonlyVec2): PlaneView | undefined {
        for (const view of this.allViews) {
            if (view.canvas) {
                const rect = view.canvas.getBoundingClientRect();
                if (
                    rect.left <= coordinates[0] &&
                    rect.right >= coordinates[0] &&
                    ((rect.bottom <= coordinates[1] &&
                        rect.top >= coordinates[1]) ||
                        (rect.top <= coordinates[1] &&
                            rect.bottom >= coordinates[1]))
                ) {
                    return view;
                }
            }
        }
        return undefined;
    }

    /**
     * Method that will receive and propagate user-triggered {@link UIEvent events}.\
     * It first distinguishes the event type,\
     * gathers additional data that is useful to handle that type of event.\
     * And then propagates the event to {@link ToolManager}.
     * @param event {@link Event} that was triggered by user interaction on the {@link ViewManager.parentElement parentElement} and caught here.
     */
    public handleEvent(event: UIEvent) {
        const keyboardEvent: KeyboardEvent = event as KeyboardEvent;
        switch (event.type) {
            case 'mousedown': {
                const mouseEvent = event as MouseEvent;
                ToolManager.syncPanMetaKey(mouseEvent.metaKey);
                // prevent browser auto-scroll on middle mouse button
                if (mouseEvent.button === 1) {
                    event.preventDefault();
                }
                const coordinates = vec2.fromValues(
                    mouseEvent.clientX,
                    mouseEvent.clientY
                );
                const view = this.getView(coordinates);
                const modifierKeys =
                    ModifierKeys.getModifierKeys(keyboardEvent);
                if (view && event.target === view.canvas) {
                    if (
                        ToolManager.onMousePress(
                            mouseEvent.button,
                            modifierKeys,
                            coordinates,
                            view
                        )
                    ) {
                        return;
                    }
                }
                break;
            }
            case 'mouseenter': {
                ToolManager.onMouseEnter();
                break;
            }
            case 'mousemove': {
                const mouseEvent = event as MouseEvent;
                ToolManager.syncPanMetaKey(mouseEvent.metaKey);
                const coordinates = vec2.fromValues(
                    mouseEvent.clientX,
                    mouseEvent.clientY
                );
                const buttons = mouseEvent.buttons;
                const movement = vec2.fromValues(
                    mouseEvent.movementX,
                    mouseEvent.movementY
                );
                const view = this.getView(coordinates);
                if (view) {
                    if (view !== this._activeView) {
                        this._activeView = view;
                    }
                    if (
                        ToolManager.onMouseMove(
                            coordinates,
                            buttons,
                            view,
                            movement
                        )
                    ) {
                        return;
                    }
                } else if (this._activeView) {
                    this._activeView = undefined;
                }
                break;
            }
            case 'mouseup': {
                const mouseEvent = event as MouseEvent;
                const coordinates = vec2.fromValues(
                    mouseEvent.clientX,
                    mouseEvent.clientY
                );
                const view = this.getView(coordinates);
                if (
                    view &&
                    ToolManager.onMouseRelease(
                        mouseEvent.button,
                        coordinates,
                        view
                    )
                ) {
                    return;
                }
                break;
            }
            case 'mouseleave':
                ToolManager.onMouseLeave();
                break;
            case 'wheel': {
                const wheelEvent = event as WheelEvent;
                const delta = wheelEvent.deltaY < 0 ? 1 : -1;
                const coordinates = vec2.fromValues(
                    wheelEvent.clientX,
                    wheelEvent.clientY
                );
                const view = this.getView(coordinates);
                if (!view) {
                    // Cursor is not over a viewport canvas — allow native
                    // scroll (e.g. table panel) to work unimpeded.
                    break;
                }
                event.preventDefault();
                if (keyboardEvent.ctrlKey) {
                    keyboardEvent.preventDefault();
                }
                const modifierKeys =
                    ModifierKeys.getModifierKeys(keyboardEvent);
                if (
                    ToolManager.onMouseWheel(
                        delta,
                        modifierKeys,
                        coordinates,
                        view
                    )
                ) {
                    return;
                }
                break;
            }
            case 'keydown': {
                const modifierKeys =
                    ModifierKeys.getModifierKeys(keyboardEvent);
                if (
                    this._activeView &&
                    ToolManager.onKeyPress(
                        keyboardEvent.key,
                        modifierKeys,
                        this._activeView
                    )
                ) {
                    event.preventDefault();
                }
                break;
            }
            case 'keyup': {
                break;
            }
            case 'dblclick': {
                const mouseEvent = event as MouseEvent;
                const coordinates = vec2.fromValues(
                    mouseEvent.clientX,
                    mouseEvent.clientY
                );
                const view = this.getView(coordinates);
                if (view && ToolManager.onDoubleClick(coordinates, view)) {
                    return;
                }
                break;
            }
            case 'contextmenu':
                event.preventDefault();
                break;
            case 'dragover':
                event.preventDefault();
                break;
            default:
                console.warn(
                    'Received unrecognize event of type ' + event.type
                );
                break;
        }
    }

    public static renderBitmapOnCanvas(image: ImageBitmap) {
        const managerInstance = ViewManager.manager;
        if (managerInstance) {
            let view = managerInstance._activeView;
            if (!view || !(view instanceof TextureView)) {
                for (const v of managerInstance.allViews) {
                    if (v instanceof TextureView) {
                        view = v;
                        break;
                    }
                }
            }
            if (view && view instanceof TextureView) {
                view.renderImageBitmap(image);
            }
        }
    }

    /**
     * Public static method that can be used to inform {@link ViewManager} about new {@link ViewUpdateEvent}s.\
     * This tells the {@link manager} singleton to {@link updateViews}.\
     * {@link manager Manager} then tries to update the views and returns the number of updated {@link View}s.
     * @param event {@link ViewUpdateEvent} that should be processed.
     * @returns Number of updated {@link View}s.\
     * 0 if no {@link View}s were updated.
     */
    public static viewUpdateEvent(event: ViewUpdateEvent): number {
        if (ViewManager.manager) {
            return ViewManager.manager.updateViews(event);
        } else {
            const eventClass = event
                ? 'type ' + event.constructor.name
                : 'undefined type';
            console.error(
                'View update event of ' +
                    eventClass +
                    ' before ViewManager singleton was initialized'
            );
            return 0;
        }
    }

    public static resetViews() {
        if (ViewManager.manager) {
            ViewManager.manager.resetViews();
        } else {
            throw new Error(
                `Attempt to reset views, while ViewManager has not been initialized`
            );
        }
    }

    public static addEventListener(
        type: string,
        listener: EventListenerOrEventListenerObject,
        options?: boolean | AddEventListenerOptions | undefined
    ): void {
        if (ViewManager.manager) {
            ViewManager.manager.addEventListener(type, listener, options);
        } else {
            throw new Error(
                `Attempt to add event listener to ViewManager, while ViewManager has not been initialized`
            );
        }
    }

    public static getDicomProcessor(): DicomProcessor {
        if (ViewManager.manager) {
            return ViewManager.manager.dicomProcessor;
        }
        throw new Error('No ViewManager initialized yet');
    }
}
